import '../App.css';
import 'react-toastify/dist/ReactToastify.css';

import * as Sentry from '@sentry/react';
import history from 'history/browser';
import { QRCodeSVG } from 'qrcode.react';
import React, { Suspense } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Trans, WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Slide } from 'react-toastify';
import { addBackToTop } from 'vanilla-back-to-top';

import {
  alertActions,
  miscActions,
  preferenceActions,
  userActions,
} from '../actions';
import Logo from '../assets/logo.svg';
import LazyPageLoader from '../components/general/LazyPageLoader';
import LoadingImage from '../components/general/LoadingImage';
import NavigateShortkeys from '../components/shortkey/NavigateShortkeys';
import ShortkeyContext, {
  getShortkeyContextValue,
  registerShortkey,
  setupShortkey,
  unregisterShortkey,
} from '../components/shortkey/ShortkeyContext';
import {
  AnalyticsProps,
  SHORT_UA,
  withAnalytics,
  withTracker,
} from '../helpers/analytics.helper';
import { StyledToastContainer } from '../helpers/toast.helper';
import { RootState } from '../reducers';
import { ReduxProps } from '../types';
import AlertBar from './General/AlertBar';
import FloatButtons from './General/FloatButtons';
import Footer from './General/Footer';
import MessageBannerTester from './General/MessageBannerTester';
import NavBar from './General/NavBar';
import Updater from './General/Updater';
import { ROUTE_PROFILE } from './RouteConstants';
import AdminRoute from './User/AdminRoute';
import PrivateRoute from './User/PrivateRoute';
import RegisteredRoute from './User/RegisteredRoute';

const TutorialFloatView = React.lazy(() => import('./Help/TutorialFloatView'));
const MessengerCustomerChat = React.lazy(
  () => import('react-messenger-customer-chat')
);
const LoginPage = React.lazy(() => import('./User/LoginPage'));
const RegisterPage = React.lazy(() => import('./User/RegisterPage'));
const ThirdPartyLandingPage = LazyPageLoader(
  React.lazy(() => import('./User/ThirdPartyLogin/LandingPage'))
);
const VerifyLoginPage = React.lazy(() => import('./User/VerifyLoginPage'));
const DashboardPage = React.lazy(() => import('./User/DashboardPage'));
const ProfilePage = React.lazy(() => import('./User/ProfilePage'));
const PaymentPage = React.lazy(() => import('./User/PaymentPage'));
const PaymentLandingPage = React.lazy(
  () => import('./User/PaymentLandingPage')
);
const PaymentThankYouPage = React.lazy(
  () => import('./User/PaymentThankYouPage')
);
const SignalPage = React.lazy(() => import('./Insight/Signal/SignalPage'));
const StartPage = React.lazy(() => import('./General/StartPage'));
const RankingPage = React.lazy(() => import('./Insight/Ranking/RankingPage'));
const CCASSPage = React.lazy(() => import('./Insight/CCASS/CCASSPage'));
const StockRankingPage = React.lazy(
  () => import('./Insight/CCASS/StockRankingPage')
);
const ParticipantRankingPage = React.lazy(
  () => import('./Insight/CCASS/ParticipantRankingPage')
);
const TOSPage = React.lazy(() => import('./Document/TOS'));
const DisclaimerPage = React.lazy(() => import('./Document/Disclaimer'));
const CopyrightPage = React.lazy(() => import('./Document/Copyright'));
const ContactPage = React.lazy(() => import('./Document/Contact'));
const PrivacyPage = React.lazy(() => import('./Document/Privacy'));
const EmailVerifiedPage = React.lazy(() => import('./User/EmailVerifiedPage'));
const VideoPage = React.lazy(() => import('./General/VideoPage'));
const TutorialPage = React.lazy(() => import('./General/TutorialPage'));
const ResetPasswordPage = React.lazy(() => import('./User/ResetPasswordPage'));
const StockPage = React.lazy(() => import('./Insight/StockPage'));
const TipsPage = React.lazy(() => import('./Tips/TipsPage'));
const ZonePage = React.lazy(() => import('./Zone/ZonePage'));
const ViewNewsPage = React.lazy(() => import('./Insight/ViewNewsPage'));
const AdminPage = React.lazy(() => import('./Admin/AdminPage'));
const SearchPage = React.lazy(() => import('./General/SearchPage'));
const TalkSignUpPage = React.lazy(() => import('./Course/SignUpPage'));
const CourseSignUpPage = React.lazy(() => import('./Course/CourseSignUpPage'));
const ProductPage = React.lazy(() => import('./General/ProductPage'));
const HelpPage = React.lazy(() => import('./Help/HelpPage'));
const TopicPage = React.lazy(() => import('./Help/TopicPage'));
const InfluencerRankingPage = React.lazy(
  () => import('./Tips/InfluencerRankingPage')
);
const WhatsappChannelPage = React.lazy(
  () => import('./WhatsappChannel/WhatsappChannelPage')
);
addBackToTop({
  id: 'back-to-top',
  backgroundColor: '#00000088',
  cornerOffset: 70, // px
  diameter: 28, // px
  zIndex: 100_000,
}); // add back to top button

const SignalPageTracked = withTracker(LazyPageLoader(SignalPage));
const StartPageTracked = withTracker(LazyPageLoader(StartPage));
const LoginPageTracked = withTracker(LazyPageLoader(LoginPage));
const RegisterPageTracked = withTracker(LazyPageLoader(RegisterPage));
const VerifyLoginPageTracked = withTracker(LazyPageLoader(VerifyLoginPage));
const DashboardPageTracked = withTracker(LazyPageLoader(DashboardPage));
const ProfilePageTracked = withTracker(LazyPageLoader(ProfilePage));
const PaymentPageTracked = withTracker(LazyPageLoader(PaymentPage));
const PaymentLandingPageTracked = withTracker(
  LazyPageLoader(PaymentLandingPage)
);
const PaymentThankYouPageTracked = withTracker(
  LazyPageLoader(PaymentThankYouPage)
);
const RankingPageTracked = withTracker(LazyPageLoader(RankingPage));
const CCASSPageTracked = withTracker(LazyPageLoader(CCASSPage));
const StockRankingPageTracked = withTracker(LazyPageLoader(StockRankingPage));
const ParticipantRankingPageTracked = withTracker(
  LazyPageLoader(ParticipantRankingPage)
);
const TOSPageTracked = withTracker(LazyPageLoader(TOSPage));
const DisclaimerPageTracked = withTracker(LazyPageLoader(DisclaimerPage));
const CopyrightPageTracked = withTracker(LazyPageLoader(CopyrightPage));
const ContactPageTracked = withTracker(LazyPageLoader(ContactPage));
const PrivacyPageTracked = withTracker(LazyPageLoader(PrivacyPage));
const EmailVerifiedPageTracked = withTracker(LazyPageLoader(EmailVerifiedPage));
const ResetPasswordPageTracked = withTracker(LazyPageLoader(ResetPasswordPage));
const VideoPageTracked = withTracker(LazyPageLoader(VideoPage));
const TutorialPageTracked = withTracker(LazyPageLoader(TutorialPage));
const HelpPageTracked = withTracker(LazyPageLoader(HelpPage));
const WhatsappChannelPageTracked = withTracker(
  LazyPageLoader(WhatsappChannelPage)
);
const HelpTopicPageTracked = withTracker(LazyPageLoader(TopicPage));
const StockPageTracked = withTracker(LazyPageLoader(StockPage));
const TipsPageTracked = withTracker(LazyPageLoader(TipsPage));
const InfluencerRankingPageTracked = withTracker(
  LazyPageLoader(InfluencerRankingPage)
);
const ZonePageTracked = withTracker(LazyPageLoader(ZonePage));
const ViewNewsPageTracked = withTracker(LazyPageLoader(ViewNewsPage));
const AdminPageTracked = withTracker(LazyPageLoader(AdminPage));
const SearchPageTracked = withTracker(LazyPageLoader(SearchPage));
const TalkSignUpPageTracked = withTracker(LazyPageLoader(TalkSignUpPage));
const CourseSignUpPageTracked = withTracker(LazyPageLoader(CourseSignUpPage));
const ProductPageTracked = withTracker(LazyPageLoader(ProductPage));

const development =
  window.location.hostname.includes('localhost') ||
  window.location.hostname.includes('dev.datalouder.com');
const ProfiledRoutes = development
  ? Routes
  : Sentry.withSentryReactRouterV6Routing(Routes);

const isBrowserTooOld = (error: Error) => {
  return String(error).includes(
    "ReferenceError: Can't find variable: ResizeObserver"
  );
};

interface States {
  hasError: false;
  sentryEventId: string;
  errorDetails: string;
  isBrowserTooOld: boolean;
}

class App extends React.Component<
  WithTranslation & ReduxProps<typeof mapStateToProps> & AnalyticsProps,
  States
> {
  constructor(props) {
    super(props);

    this.state = {
      hasError: false,
      sentryEventId: undefined,
      errorDetails: undefined,
      isBrowserTooOld: false,
    };

    const { dispatch, region } = this.props;

    const init = ({ location }) => {
      // clear alert on location change
      dispatch(alertActions.clear());
      // detect region
      const tokens = location.pathname.split('/');
      if (
        tokens.length > 1 &&
        ['hk', 'us'].includes(tokens[1]) &&
        tokens[1] !== region
      ) {
        dispatch(preferenceActions.setRegion(tokens[1]));
      }
    };
    init(window);
    history.listen(init);

    setupShortkey.call(this);
  }

  registerShortkey = registerShortkey.bind(this);
  unregisterShortkey = unregisterShortkey.bind(this);
  getShortkeyContextValue = getShortkeyContextValue.bind(this);

  showEmailVerification = () => {
    const { t, user, resending, resent, dispatch } = this.props;

    if (user.user && !user.user.loginVerified) {
      return (
        <Container>
          <Row>
            <Col md={{ span: 8, offset: 2 }}>
              <Alert variant="warning">
                {user.user.login.includes('@') ? (
                  <div>
                    <p>{t('email-verify-1')}</p>
                    <p>{t('email-verify-2')}</p>
                  </div>
                ) : (
                  <div>
                    <p>{t('sms-verify')}</p>
                  </div>
                )}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() =>
                    dispatch(userActions.resendVerificationCommunicate())
                  }
                >
                  {user.user.login.includes('@')
                    ? t('email-verify-button')
                    : t('sms-verify-button')}{' '}
                  <LoadingImage loading={resending} />
                </Button>
                {resent && (
                  <div>
                    <br />
                    {user.user.login.includes('@')
                      ? t('email-verify-resent')
                      : t('sms-verify-resent')}
                  </div>
                )}
              </Alert>
            </Col>
          </Row>
        </Container>
      );
    }
    return <></>;
  };

  fetchLoggedInOnlyApis() {
    const { dispatch } = this.props;
    dispatch(userActions.refresh());
    dispatch(miscActions.loadPreference());
  }

  componentDidMount() {
    const { user, t } = this.props;

    // login only
    if (user.user) {
      this.fetchLoggedInOnlyApis();
    }

    const observer = new MutationObserver(function () {
      if (document.documentElement.className.match('translated')) {
        alert(t('no-google-translate'));
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
      childList: false,
      characterData: false,
    });
    // setup analytics flush scheduler
    setInterval(() => this.props.flush(), 5000);
  }

  componentDidUpdate(prevProps) {
    const { user } = this.props;

    if (user.user) {
      Sentry.configureScope(scope =>
        scope.setUser({
          email: user.user.login,
          id: String(user.user.id),
          username: user.user.alias,
        })
      );
      // sync login only data after login
      if (!prevProps.user.user) {
        this.fetchLoggedInOnlyApis();
      }
    }
  }

  // handle errors
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    Sentry.withScope(scope => {
      scope.setExtras(errorInfo);
      const sentryEventId = Sentry.captureException(error);
      const errorDetails = btoa(
        JSON.stringify({
          error: String(error),
          stack: String(error?.stack).slice(0, 200),
          errorInfo: String(errorInfo),
          sentryEventId,
          ua: SHORT_UA,
        })
      );
      this.setState({
        sentryEventId,
        errorDetails,
        isBrowserTooOld: isBrowserTooOld(error),
      });
    });
  }

  render() {
    // handle errors
    if (this.state.hasError) {
      return (
        <Container>
          <Row>
            <Col>
              <h1>
                <img src={Logo} style={{ height: '2.5rem' }} />{' '}
                <Trans i18nKey="oops" />
              </h1>
              {this.state.isBrowserTooOld && (
                <p>
                  <Trans i18nKey="your-browser-is-too-old" />
                </p>
              )}
              {this.state.sentryEventId && (
                <p>
                  <Trans
                    i18nKey="issue-reported"
                    values={{ eventId: this.state.sentryEventId }}
                    components={{ b: <b /> }}
                  />

                  <div className="text-center">
                    <hr />
                    {this.state.errorDetails && (
                      <QRCodeSVG
                        style={{
                          height: 'auto',
                          maxWidth: '512px',
                          width: '100%',
                        }}
                        value={this.state.errorDetails}
                      />
                    )}
                  </div>
                </p>
              )}
            </Col>
          </Row>
        </Container>
      );
    }
    const { user } = this.props;
    const authed = user.user ? true : false;
    const isAdmin = authed && user.user.isAdmin;
    const paid = user.user && user.user.hasMembership;
    return (
      <ShortkeyContext.Provider value={this.getShortkeyContextValue()}>
        <BrowserRouter>
          <div id="top-container">
            <div id="body-container">
              <NavBar />
              <AlertBar />
              {this.showEmailVerification()}
              <ProfiledRoutes>
                <Route path="/" element={<StartPageTracked />} />
                <Route path="/login" element={<LoginPageTracked />} />
                <Route
                  path="/landing/:provider"
                  element={<ThirdPartyLandingPage />}
                />
                <Route path="/register" element={<RegisterPageTracked />} />
                <Route
                  path="/v/:userId/:token"
                  element={<VerifyLoginPageTracked />}
                />
                <Route path="/term-of-service" element={<TOSPageTracked />} />
                <Route path="/disclaimer" element={<DisclaimerPageTracked />} />
                <Route path="/copyright" element={<CopyrightPageTracked />} />
                <Route path="/contact-us" element={<ContactPageTracked />} />
                <Route
                  path="/privacy-policy"
                  element={<PrivacyPageTracked />}
                />
                <Route
                  path="/email-verified"
                  element={<EmailVerifiedPageTracked />}
                />
                <Route path="/video" element={<VideoPageTracked />} />
                <Route path="/tutorial" element={<TutorialPageTracked />} />
                <Route path="/help" element={<HelpPageTracked />} />
                <Route
                  path="/whatsapp-channel"
                  element={<WhatsappChannelPageTracked />}
                />
                <Route path="/help/:id" element={<HelpTopicPageTracked />} />
                <Route
                  path="/reset-password"
                  element={<ResetPasswordPageTracked />}
                />
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute
                      paid={paid}
                      authed={authed}
                      element={<DashboardPageTracked />}
                    />
                  }
                />
                <Route
                  path={ROUTE_PROFILE}
                  element={
                    <RegisteredRoute
                      authed={authed}
                      element={<ProfilePageTracked />}
                    />
                  }
                />
                <Route
                  path="/payment"
                  element={
                    <RegisteredRoute
                      authed={authed}
                      element={<PaymentPageTracked />}
                    />
                  }
                />
                <Route
                  path="/payment/thankyou"
                  element={
                    <RegisteredRoute
                      authed={authed}
                      element={<PaymentThankYouPageTracked />}
                    />
                  }
                />
                <Route
                  path="/from/:promo"
                  element={
                    <RegisteredRoute
                      authed={authed}
                      element={<PaymentLandingPageTracked />}
                    />
                  }
                />
                <Route
                  path="/:region/ranking"
                  element={<RankingPageTracked />}
                />
                <Route path="/lookup" element={<StockPageTracked />} />
                <Route
                  path="/lookup/:stockCode"
                  element={<StockPageTracked />}
                />
                <Route
                  path="/news/:newsId"
                  element={
                    <PrivateRoute
                      paid={paid}
                      authed={authed}
                      element={<ViewNewsPageTracked />}
                    />
                  }
                />
                <Route path="/tips" element={<TipsPageTracked />} />
                <Route
                  path="/:region/influencer-ranking"
                  element={<InfluencerRankingPageTracked />}
                />
                <Route path="/zone" element={<ZonePageTracked />} />
                <Route path="/hk/ccass" element={<CCASSPageTracked />} />
                <Route
                  path="/hk/ccass/stock-ranking"
                  element={<StockRankingPageTracked />}
                />
                <Route
                  path="/hk/ccass/participant-ranking"
                  element={<ParticipantRankingPageTracked />}
                />
                <Route path="/:region/signal" element={<SignalPageTracked />} />
                <Route
                  path="/admin"
                  element={
                    <AdminRoute
                      isAdmin={isAdmin}
                      element={<AdminPageTracked />}
                    />
                  }
                />
                <Route
                  path="/search"
                  element={
                    <AdminRoute
                      isAdmin={isAdmin}
                      element={<SearchPageTracked />}
                    />
                  }
                />
                <Route
                  path="/course/datalouder-talk"
                  element={<TalkSignUpPageTracked />}
                />
                <Route
                  path="/course/datalouder-course"
                  element={<CourseSignUpPageTracked />}
                />
                <Route
                  path="/course/datalouder-course/:courseCode"
                  element={<CourseSignUpPageTracked />}
                />
                <Route path="/product" element={<ProductPageTracked />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </ProfiledRoutes>
              <Updater />
            </div>
            <Footer />
            <Suspense fallback={<span className="hidden" />}>
              <MessengerCustomerChat
                pageId="101560234565618"
                appId="2230974307181839"
              />
            </Suspense>
            <FloatButtons />
            <NavigateShortkeys />
            <Suspense fallback={<span className="hidden" />}>
              <TutorialFloatView />
            </Suspense>
            <MessageBannerTester />
            <StyledToastContainer transition={Slide} />
          </div>
        </BrowserRouter>
      </ShortkeyContext.Provider>
    );
  }
}

function mapStateToProps(state: RootState) {
  const { alert, user, preference, userExt } = state;
  const { region, fontSize } = preference;
  const {
    emailVerify: { loading: resending, resent },
  } = userExt;
  return {
    alert,
    user,
    region,
    fontSize,
    resending,
    resent,
  };
}

export default withAnalytics(withTranslation()(connect(mapStateToProps)(App)));
